import { normalizeContentfulText } from '@sola/ark-skippy/utils/text-utils.js';
import { keys } from '@sola/contentful-model/keys.js';
import { getArticlePath } from '@sola/contentful-model/lib/article.js';
import { ArticleTypesEnum } from '@sola/contentful-model/lib/taxonomy-values.js';
import {
  ContentResponseDto,
  JsonFieldFundSettings,
  JsonLink,
  LinkType,
  LinkedContentResponseDto,
  Media,
  ReferencedContentResponseDto,
} from '@sola/schema/lib/contentful.js';
import {
  FlexibleContentProps,
  FundProps,
  Link,
  MediaProps,
} from '@sola/schema/lib/schema.js';
import { removeNullValues } from '@sola/utils/object.js';
import { trailingSlash } from '@sola/utils/string.js';
import { isAbsoluteInternalLink } from '@sola/utils/url.js';

export const convertContentResponseToLink = (
  props?: JsonLink | null,
  linkedContent?: LinkedContentResponseDto,
  siteRootUrl = '',
): Link | undefined => {
  if (!props) {
    return undefined;
  }
  const title = props?.type !== LinkType.enum.none && props?.title;

  if (props?.type === LinkType.enum.external) {
    const isInternal = isAbsoluteInternalLink(props.externalUrl);
    return {
      link: props.externalUrl || '#invalid-link',
      title: title || '',
      target: !isInternal ? '_blank' : '',
    };
  }
  if (props?.type === LinkType.enum.download) {
    const linkedContentItem = linkedContent?.items?.find(
      (item) => item?.sys?.id === props.download.id,
    );

    const path = `/file/${linkedContentItem?.sys.id}`;
    return {
      // @TODO: How should we handle errors?
      link: `${siteRootUrl}${path}` || '#unknown-link',
      title: title || '',
      target: '_blank',
    };
  }
  if (props?.type === LinkType.enum.internal) {
    const linkedContentItem = linkedContent?.items?.find(
      (item) => item?.sys?.id === props.entry.id,
    );

    let path = trailingSlash(`${linkedContentItem?.path}`);
    let isDownload = false;

    if (
      linkedContentItem &&
      props.entry.type === keys.contentTypes.article &&
      linkedContentItem.slug
    ) {
      path = trailingSlash(
        getArticlePath(
          linkedContentItem.slug,
          linkedContentItem.taxonomyArticleType as ArticleTypesEnum,
        ),
      );
    }
    if (linkedContentItem && props.entry.type === keys.contentTypes.downloads) {
      path = `/file/${props.entry.id}`;
      isDownload = true;
    }

    if (props.parameters) {
      path += `?${props.parameters}`;
    }

    return {
      // @TODO: How should we handle errors?
      link: path ? `${siteRootUrl}${path}` : '#unknown-link',
      title: title || '',
      target: isDownload ? '_blank' : '',
    };
  }
  if (props?.type === LinkType.enum.anchor) {
    return {
      link: `#${props.anchor}`,
      title: title || '',
    };
  }
  if (props?.type === LinkType.enum.mailto) {
    return {
      link: `mailto:${props.mailto}`,
      title: title || '',
    };
  }

  return undefined;
};

export const convertMediaResponseToMedia = (source: Media) => {
  if (!source) {
    return undefined;
  }
  const media = {
    caption: source.caption || undefined,
    image: source.image
      ? {
          ...source.image,
          alt: source.altText || '',
          focus: source.imageFocalPoint?.focalPoint || undefined,
          ratio: source.imageFocalPoint?.ratio || undefined,
        }
      : undefined,
    video: source.video || undefined,
  };

  return media;
};

export const convertContentResponseToFund = (
  props?: JsonFieldFundSettings | null,
  referencedContent?: ReferencedContentResponseDto,
): FundProps | undefined => {
  if (!props || !props.entry) {
    return undefined;
  }

  const fund = referencedContent?.items?.find(
    (item) => item?.sys?.id === props.entry?.id,
  );

  const catalogAmount =
    props?.suggestedAmount ??
    props?.suggestedAmountsOneTime?.find((item) => item.isDefault)?.value ??
    props?.suggestedAmountsMonthly?.find((item) => item.isDefault)?.value ??
    props?.suggestedAmountsAnnually?.find((item) => item.isDefault)?.value;

  const fundData = {
    id: props.entry?.id,
    ...fund,
    suggestedAmount: props.suggestedAmount || undefined,
    suggestedAmountsOneTime: props.suggestedAmountsOneTime || undefined,
    suggestedAmountsMonthly: props.suggestedAmountsMonthly || undefined,
    suggestedAmountsAnnually: props.suggestedAmountsAnnually || undefined,
    catalogAmount: catalogAmount || undefined,
  };

  const cleanedFundData = removeNullValues(fundData);

  return FundProps.parse(cleanedFundData);
};

export const convertContentResponseToFlexibleContent = (
  source: ContentResponseDto,
): FlexibleContentProps | undefined => {
  if (!source) {
    return;
  }

  const media = convertMediaResponseToMedia(source.media as Media);
  const parsedMedia = MediaProps.safeParse(media);

  const mediaLogo = convertMediaResponseToMedia(source.mediaLogo as Media);
  const parsedMediaLogo = MediaProps.safeParse(mediaLogo);

  const displayDate = source.displayDate || source.sys.publishedAt;

  return FlexibleContentProps.parse({
    title: normalizeContentfulText(source.title || undefined),
    subtitle: normalizeContentfulText(source.subtitle || undefined),
    icon: source.icon || undefined,
    badge: source.badge || undefined,
    quotation: source.quotation || undefined,
    logo: source.logo || undefined,
    primaryCTA:
      convertContentResponseToLink(
        source.primaryCTA || source.primaryCta,
        source.linkedContentCollection,
      ) || undefined,
    secondaryCTA:
      convertContentResponseToLink(
        source.secondaryCTA || source.secondaryCta,
        source.linkedContentCollection,
      ) || undefined,
    tertiaryCTA:
      convertContentResponseToLink(
        source.tertiaryCTA || source.tertiaryCta,
        source.linkedContentCollection,
      ) || undefined,
    media: parsedMedia?.data,
    mediaLogo: parsedMediaLogo?.data,
    displayDate: displayDate || undefined,
    fund:
      convertContentResponseToFund(
        source.fund,
        source.referencedContentCollection,
      ) || undefined,
  });
};
